import React, { useEffect, useRef, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  CheckoutOneOffCardForm,
  CheckoutOneOffCardFormHandle,
  OneOffPaymentMethodResult,
} from "./CheckoutOneOffCardForm";
import { CheckoutReplacePaymentMethodDialog } from "./CheckoutReplacePaymentMethodDialog";
import { saveCheckoutCardToBankAccount } from "./checkoutSaveCard";
import { SavedBankAccountForLabel } from "./checkoutPaymentLabels";
import { useAuth } from "../../hooks/useAuth";
import { useMutationUpdateBankAccount } from "../../queries/bankAccount/hooks/useMutationUpdateBankAccount";
import { BANK_ACCOUNT } from "../../queries/pharmacy/pharmacyTypeDefs";

type CheckoutOneOffCardDialogProps = {
  open: boolean;
  buyerEmail?: string;
  submitting?: boolean;
  savedBankAccount: SavedBankAccountForLabel & {
    id: number;
    accountId?: string | null;
  };
  pharmacyId: number;
  userId: number;
  onClose: () => void;
  /** Called after order path is chosen; omit paymentMethodId when card was saved to Bank Account. */
  onConfirm: (paymentMethodId?: string) => Promise<void>;
  onCardSaved?: () => void;
};

export const CheckoutOneOffCardDialog: React.FunctionComponent<CheckoutOneOffCardDialogProps> = ({
  open,
  buyerEmail,
  submitting = false,
  savedBankAccount,
  pharmacyId,
  userId,
  onClose,
  onConfirm,
  onCardSaved,
}) => {
  const stripe = useStripe();
  const { token } = useAuth();
  const oneOffCardRef = useRef<CheckoutOneOffCardFormHandle>(null);
  const confirmInFlightRef = useRef(false);
  const replaceConfirmInFlightRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<OneOffPaymentMethodResult | null>(null);
  const [savingCard, setSavingCard] = useState(false);

  useEffect(() => {
    if (!open) {
      confirmInFlightRef.current = false;
      replaceConfirmInFlightRef.current = false;
      setConfirming(false);
    }
  }, [open]);

  const [updateBankAccount] = useMutationUpdateBankAccount({
    refetchQueries: [
      {
        query: BANK_ACCOUNT,
        variables: {
          where: {
            AND: [
              { object: { in: ["payment_method", "card"] } },
              { userId: { equals: userId } },
              { status: { in: ["VERIFIED", "APPROVED"] } },
              { accountId: { not: { equals: null } } },
            ],
          },
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const cardDialogBusy = submitting || savingCard || confirming;

  const handleConfirm = async () => {
    if (!oneOffCardRef.current || cardDialogBusy || confirmInFlightRef.current) {
      return;
    }
    confirmInFlightRef.current = true;
    setConfirming(true);
    setError(null);
    let awaitingReplaceConfirm = false;
    try {
      const result = await oneOffCardRef.current.createOneOffPaymentMethod();

      if (oneOffCardRef.current.getSaveCard()) {
        awaitingReplaceConfirm = true;
        setPendingPayment(result);
        setReplaceDialogOpen(true);
        return;
      }

      await onConfirm(result.paymentMethodId);
    } catch (err: any) {
      setError(err?.message || "Could not use this card.");
    } finally {
      if (!awaitingReplaceConfirm) {
        confirmInFlightRef.current = false;
        setConfirming(false);
      }
    }
  };

  const handleReplaceConfirm = async () => {
    if (
      !pendingPayment ||
      !stripe ||
      !token ||
      savingCard ||
      submitting ||
      replaceConfirmInFlightRef.current
    ) {
      return;
    }
    replaceConfirmInFlightRef.current = true;
    setSavingCard(true);
    setError(null);
    try {
      await saveCheckoutCardToBankAccount({
        stripe,
        token,
        paymentMethodId: pendingPayment.paymentMethodId,
        cardholderName: pendingPayment.cardholderName,
        cardBrand: pendingPayment.brand,
        cardLast4: pendingPayment.last4,
        bankAccountId: savedBankAccount.id,
        pharmacyId,
        userId,
        oldAccountId: savedBankAccount.accountId,
        updateBankAccount,
      });

      setReplaceDialogOpen(false);
      setPendingPayment(null);
      onCardSaved?.();
      await onConfirm();
    } catch (err: any) {
      setError(err?.message || "Could not save card.");
      setReplaceDialogOpen(false);
    } finally {
      replaceConfirmInFlightRef.current = false;
      setSavingCard(false);
      confirmInFlightRef.current = false;
      setConfirming(false);
    }
  };

  const handleReplaceCancel = () => {
    if (savingCard) {
      return;
    }
    setReplaceDialogOpen(false);
    setPendingPayment(null);
    confirmInFlightRef.current = false;
    setConfirming(false);
  };

  const handleClose = () => {
    if (cardDialogBusy) {
      return;
    }
    setError(null);
    setReplaceDialogOpen(false);
    setPendingPayment(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
      >
        <DialogTitle>Enter card details</DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          {open && <CheckoutOneOffCardForm ref={oneOffCardRef} defaultEmail={buyerEmail} />}
          {error && (
            <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={cardDialogBusy}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleConfirm} disabled={cardDialogBusy}>
            {cardDialogBusy ? "Confirming…" : "Confirm Order"}
            {cardDialogBusy && <CircularProgress sx={{ ml: 1 }} size={14} color="inherit" />}
          </Button>
        </DialogActions>
      </Dialog>

      {pendingPayment && (
        <CheckoutReplacePaymentMethodDialog
          open={replaceDialogOpen}
          saving={savingCard}
          currentMethod={savedBankAccount}
          newCardBrand={pendingPayment.brand}
          newCardLast4={pendingPayment.last4}
          onCancel={handleReplaceCancel}
          onConfirm={handleReplaceConfirm}
        />
      )}
    </>
  );
};

export default CheckoutOneOffCardDialog;
